// Updated version of VendorLedgerReport based on your clarified rules
import React, { useEffect, useState } from 'react';
import Footer from '../../layout/Footer';
import { Container, Row, Col, Card, Form, Table } from 'react-bootstrap';
import $ from "jquery";
import DataTable from 'datatables.net-dt';
import { getFromAPI } from '../../../Utils/utils';

export default function VendorLedgerReport({ tableName = "Leave Report" }) {
    const current_date = new Date();
    const [selectedMonth, setSelectedMonth] = useState(current_date.getMonth());
    const [selectedYear, setSelectedYear] = useState(current_date.getFullYear());
    const [adminLeaveData, setAdminApprovalLeaveData] = useState([]);
    const [deletedStaffFromAPI, setDeletedStaffFromAPI] = useState([]);
    const [processedLeaveData, setProcessedLeaveData] = useState([]);
    const [processedExEmployeeData, setProcessedExEmployeeData] = useState([]);

    const getMonthName = (monthNum) => {
        const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        return monthNum === "All" ? "All Months" : months[parseInt(monthNum)];
    };

    const getAllStaffData = async () => {
        try {
            const currentResponse = await getFromAPI('leave_report/');
            const currentList = Array.isArray(currentResponse) ? currentResponse : (currentResponse?.data || currentResponse?.results || []);
            setAdminApprovalLeaveData(Array.isArray(currentList) ? currentList : []);

            let deletedList = [];
            try {
                const deletedResponse = await getFromAPI('leave_report/', undefined, { isdeleted: 1 });
                deletedList = Array.isArray(deletedResponse) ? deletedResponse : (deletedResponse?.data || deletedResponse?.results || []);
                deletedList = Array.isArray(deletedList) ? deletedList : [];
            } catch (_) {}
            setDeletedStaffFromAPI(deletedList);
        } catch (error) {
            console.error('Error fetching staff data:', error);
            setAdminApprovalLeaveData([]);
            setDeletedStaffFromAPI([]);
        }
    };

    const calculateMonthLeave = (leaveData, selectedMonth, selectedYear) => {
        let totalLeaveConsumed = 0;
        const monthNum = selectedMonth === "All" ? -1 : Number(selectedMonth);
        const yearNum = selectedYear === "All" ? -1 : Number(selectedYear);

        leaveData.forEach((leave) => {
            const startDate = new Date(leave.leave_start_date);
            const endDate = new Date(leave.leave_end_date);
            if (isNaN(startDate) || isNaN(endDate)) return;

            let allDates = [];
            let d = new Date(startDate);
            while (d <= endDate) {
                allDates.push(new Date(d));
                d.setDate(d.getDate() + 1);
            }

            let leaveParts = [];
            if (Array.isArray(leave.leave_part_selection)) leaveParts = leave.leave_part_selection;
            else if (typeof leave.leave_part_selection === 'string') {
                try { leaveParts = JSON.parse(leave.leave_part_selection); } catch { leaveParts = leave.leave_part_selection.split(","); }
            }
            if (leaveParts.length !== allDates.length) leaveParts = Array(allDates.length).fill("Full Day");

            allDates.forEach((date, idx) => {
                const monthMatch = monthNum === -1 || date.getMonth() === monthNum;
                const yearMatch = yearNum === -1 || date.getFullYear() === yearNum;
                if (monthMatch && yearMatch) {
                    const part = (leaveParts[idx] || "None").trim();
                    if (part === "Weekend" || part === "Holiday") return;
                    if (part === "Full Day" || part === "None") totalLeaveConsumed += 1;
                    else if (part === "First Half" || part === "Second Half") totalLeaveConsumed += 0.5;
                }
            });
        });

        return Math.round(totalLeaveConsumed * 2) / 2;
    };

    // Leave year: April to March. Cumulative leave from April of leave year through selected month.
    const calculateCumulativeLeaveThroughMonth = (leaveData, throughMonth, year) => {
        if (throughMonth === "All" || year === "All") return calculateMonthLeave(leaveData, "All", "All");
        const m = Number(throughMonth);
        const y = Number(year);
        const startYear = m >= 3 ? y : y - 1;
        let cumulative = 0;
        for (let yr = startYear; yr <= y; yr++) {
            const startM = yr === startYear ? 3 : 0;
            const endM = yr === y ? m : 11;
            for (let mo = startM; mo <= endM; mo++) {
                cumulative += calculateMonthLeave(leaveData, mo, yr);
            }
        }
        return Math.round(cumulative * 2) / 2;
    };

    // Accrued leaves by end of selected month in leave year (Apr=1, May=2, ..., Mar=12)
    const getAccruedLeavesByMonth = (month, year) => {
        if (month === "All" || year === "All") return 0;
        const posInLeaveYear = (Number(month) + 9) % 12;
        return posInLeaveYear + 1;
    };

    // Carry from previous leave year (unused leave at end of March added to April)
    const getCarryFromPrevLeaveYear = (leaveData, year) => {
        if (year === "All") return 0;
        const cumulativeThroughMarch = calculateCumulativeLeaveThroughMonth(leaveData, 2, Number(year));
        return Math.max(0, Math.round((12 - cumulativeThroughMarch) * 2) / 2);
    };

    const buildRows = (staffList, exEmployeeOnly = false) => {
        const rows = [];
        const useMonthlyAccrual = selectedMonth !== "All" && selectedYear !== "All";
        for (const staff of staffList || []) {
            const allLeaves = staff.leaves || [];
            const filteredLeaves = allLeaves.filter(leave => ["Approved", "Pending", "Partially Approved"].includes(leave.leave_status));
            const monthlyConsumedLeave = calculateMonthLeave(filteredLeaves, selectedMonth, selectedYear);
            const staffName = staff.staff_name || staff.firstname || "";

            if (exEmployeeOnly) {
                rows.push([staffName, monthlyConsumedLeave]);
                continue;
            }

            let totalAvailable;   // total leave entitlement (accrued + carry or opening balance)
            let balanceValue;     // actual balance = totalAvailable - leave taken (can be negative)
            let leaveDeducted;
            let balanceLeaveDisplay; // Balance Leave column: remaining leave, 0 when negative
            if (useMonthlyAccrual) {
                const accrued = getAccruedLeavesByMonth(selectedMonth, selectedYear);
                const cumulativeTaken = calculateCumulativeLeaveThroughMonth(filteredLeaves, selectedMonth, selectedYear);
                const m = Number(selectedMonth);
                const carry = m >= 3 ? getCarryFromPrevLeaveYear(filteredLeaves, selectedYear) : 0;
                totalAvailable = carry + accrued;
                balanceValue = totalAvailable - cumulativeTaken;
                leaveDeducted = balanceValue < 0 ? Math.round(balanceValue * 2) / 2 : 0;
                balanceLeaveDisplay = balanceValue >= 0 ? Math.round(balanceValue * 2) / 2 : 0;
            } else {
                const totalLeave = staff.opening_balance?.total_leave || 0;
                const totalConsumedLeave = calculateMonthLeave(filteredLeaves, "All", "All");
                totalAvailable = totalLeave;
                balanceValue = totalLeave - totalConsumedLeave;
                leaveDeducted = balanceValue < 0 ? Math.round(balanceValue * 2) / 2 : 0;
                balanceLeaveDisplay = balanceValue >= 0 ? Math.round(balanceValue * 2) / 2 : 0;
            }
            rows.push([staffName, monthlyConsumedLeave, leaveDeducted, balanceLeaveDisplay]);
        }
        return rows;
    };

    const processLeaveData = () => {
        setProcessedLeaveData(buildRows(adminLeaveData));
        setProcessedExEmployeeData(buildRows(deletedStaffFromAPI));
    };

    useEffect(() => { getAllStaffData(); }, []);

    useEffect(() => {
        processLeaveData();
    }, [adminLeaveData, deletedStaffFromAPI, selectedMonth, selectedYear]);

    const leaveTakenColTitle = selectedMonth === "All" && selectedYear === "All" ? "Total Leave Taken" : `Leave Taken (${selectedMonth === "All" ? "All Months" : getMonthName(selectedMonth)}${selectedYear !== "All" ? ` ${selectedYear}` : ""})`;
    const tableOptions = { dom: "Bflrtip", pageLength: 25, autoWidth: false, language: { search: '', searchPlaceholder: "Search...", paginate: { previous: '«', next: '»' } } };

    useEffect(() => {
        return () => {
            if ($.fn.dataTable.isDataTable('#mainDatatableLeaveList')) $('#mainDatatableLeaveList').DataTable().destroy();
            if ($.fn.dataTable.isDataTable('#mainDatatableLeaveListEx')) $('#mainDatatableLeaveListEx').DataTable().destroy();
        };
    }, []);

    useEffect(() => {
        if ($.fn.dataTable.isDataTable('#mainDatatableLeaveList')) $('#mainDatatableLeaveList').DataTable().destroy();
        $('#mainDatatableLeaveList').DataTable({ ...tableOptions, data: processedLeaveData, columns: [{ title: "Employee Name" }, { title: leaveTakenColTitle }, { title: "Leave Deducted" }, { title: "Balance Leave" }] });
    }, [processedLeaveData, selectedMonth, selectedYear]);

    useEffect(() => {
        if ($.fn.dataTable.isDataTable('#mainDatatableLeaveListEx')) $('#mainDatatableLeaveListEx').DataTable().destroy();
        $('#mainDatatableLeaveListEx').DataTable({ ...tableOptions, data: processedExEmployeeData, columns: [{ title: "Employee Name" }, { title: leaveTakenColTitle }] });
    }, [processedExEmployeeData, selectedMonth, selectedYear]);

    const handleMonthChange = (e) => setSelectedMonth(e.target.value);
    const handleYearChange = (e) => setSelectedYear(e.target.value === "All" ? "All" : Number(e.target.value));

    const getYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let y = currentYear + 1; y >= currentYear - 10; y--) years.push(y);
        return years;
    };

    return (
        <div className="page-wrapper">
            <div className='page-content'>
                <Container>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body>
                                    <Card.Title>{tableName}</Card.Title>
                                    <Form>
                                        <div className='d-md-flex align-items-center gap-3 flex-wrap'>
                                            <div className="mb-3 me-sm-2" style={{ width: '120px' }}>
                                                <Form.Label className="small">Month</Form.Label>
                                                <Form.Select size="sm" name='selectedMonth' id='selectedMonth' value={selectedMonth === "All" ? "All" : String(selectedMonth)} onChange={handleMonthChange} style={{ width: '100%' }}>
                                                    <option value="All">All Months</option>
                                                    {Array.from({ length: 12 }, (_, i) => <option key={i} value={String(i)}>{getMonthName(i)}</option>)}
                                                </Form.Select>
                                            </div>
                                            <div className="mb-3" style={{ width: '100px' }}>
                                                <Form.Label className="small">Year</Form.Label>
                                                <Form.Select size="sm" name='selectedYear' id='selectedYear' value={selectedYear === "All" ? "All" : String(selectedYear)} onChange={handleYearChange} style={{ width: '100%' }}>
                                                    <option value="All">All Years</option>
                                                    {getYearOptions().map((y) => <option key={y} value={String(y)}>{y}</option>)}
                                                </Form.Select>
                                            </div>
                                        </div>
                                    </Form>
                                    <h6 className="mb-2">Current Employees</h6>
                                    <Card className='border shadow-none mb-4'>
                                        <Card.Body>
                                            <Table responsive bordered className='table-nowrap' id='mainDatatableLeaveList'>
                                                <thead className='table-light'>
                                                    <tr><th>Employee Name</th><th>{leaveTakenColTitle}</th><th>Leave Deducted</th><th>Balance Leave</th></tr>
                                                </thead>
                                            </Table>
                                        </Card.Body>
                                    </Card>
                                    <h6 className="mb-2">Ex-Employees</h6>
                                    <Card className='border shadow-none mb-0'>
                                        <Card.Body>
                                            <Table responsive bordered className='table-nowrap' id='mainDatatableLeaveListEx'>
                                                <thead className='table-light'>
                                                    <tr><th>Employee Name</th><th>{leaveTakenColTitle}</th></tr>
                                                </thead>
                                            </Table>
                                        </Card.Body>
                                    </Card>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
            <Footer />
        </div>
    );
}
